import { useState } from "react";
import {
    Stack,
    DefaultButton,
    PrimaryButton,
    Label,
    Text,
    MessageBar,
    MessageBarType
} from "@fluentui/react";
import { useBeforeUnloadGuard } from "../../../hooks/useBeforeUnloadGuard";
import { FileInfo } from "../../../api/admin/models_admin";
import { displayFileName } from "../components";
import { ApplyModalFrame, ApplyResultMessageBar, IndexerRunOutcome, IndexingPane, runIndexerForApply } from "./apply_common";
import { DeleteFilePane, DeleteResult } from "./delete_file_pane";
import { FileInfoUI, FileUploadInfo } from "./upload_file_status";

export interface ApplyChangesResult {
    // 実行に入らずキャンセルされた場合はfalse。この場合は保留をそのまま残す
    executed: boolean
    // インデクサの実行(＝検索への反映)まで到達したか
    indexerStarted: boolean
    message: string
    // 実行できなかった操作。呼び出し側で保留に戻し、やり直せるようにする
    failedDeletions: FileInfo[]
    failedUploads: File[]
}

/**
 * 保留していた操作を「確認 → 削除 → アップロード → 検索へ反映」の順にまとめて実行する。
 *
 * アップロード・削除のたびにインデクサを実行すると、AI Searchのインデクサは同時実行できず
 * 409が返ってBlobと索引がずれるため、実行の最後に1回だけインデクサを走らせる。
 */
type Phase = "confirm" | "deleting" | "uploading" | "indexing" | "done"

export const ApplyChangesModal = (props: {
    containerSeq: number
    containerName: string
    deletions: FileInfo[]
    uploads: File[]
    onFinished: (result: ApplyChangesResult) => void
}) => {
    const { containerSeq, containerName, deletions, uploads, onFinished } = props

    const [phase, setPhase] = useState<Phase>("confirm")
    const [deleteResult, setDeleteResult] = useState<DeleteResult | null>(null)
    const [uploadResult, setUploadResult] = useState<FileUploadInfo[] | null>(null)
    const [indexerOutcome, setIndexerOutcome] = useState<IndexerRunOutcome | null>(null)
    // アップロード・整形処理の実行中かどうか。実行中は離脱・中断させない
    const [uploadBusy, setUploadBusy] = useState(false)

    // 実行中はタブを閉じられると処理が中断されるため、離脱前に確認する
    const isRunning = phase === "deleting" || phase === "uploading" || phase === "indexing"
    useBeforeUnloadGuard(isRunning)

    // 削除・アップロードのどちらも無い場合は、検索への反映だけを行う
    const startApply = () => {
        if (0 < deletions.length) {
            setPhase("deleting")
        } else if (0 < uploads.length) {
            setPhase("uploading")
        } else {
            runIndexerPhase()
        }
    }

    const handleDeleteComplete = (result: DeleteResult) => {
        setDeleteResult(result)
        if (0 < uploads.length) {
            setPhase("uploading")
        } else {
            runIndexerPhase()
        }
    }

    const handleUploadComplete = (result: FileUploadInfo[]) => {
        setUploadResult(result)
        runIndexerPhase()
    }

    const runIndexerPhase = async () => {
        setPhase("indexing")
        setIndexerOutcome(await runIndexerForApply(containerSeq))
        setPhase("done")
    }

    const failedDeletions = deleteResult?.failedFiles ?? []
    // アップロードに失敗したファイルは、選択し直さずにやり直せるよう元のFileを返す
    const failedUploadNames = new Set((uploadResult ?? []).filter(v => v.status === "uploadFailed").map(v => v.filename))
    const failedUploads = uploads.filter(file => failedUploadNames.has(file.name))

    const deletedCount = deleteResult?.deletedFileSeq.length ?? 0
    const deleteFailedCount = failedDeletions.length
    const uploadedCount = uploadResult?.filter(v => v.status !== "waiting" && v.status !== "uploadFailed").length ?? 0
    const uploadFailedCount = failedUploads.length

    return (
        <ApplyModalFrame containerName={containerName}>
            {phase === "confirm" && (
                <Stack tokens={{ childrenGap: 16 }} styles={{ root: { flex: 1, minHeight: 0 } }}>
                    <MessageBar messageBarType={MessageBarType.severeWarning}>
                        以下の変更を実行し、検索へ反映します。削除したファイルは元に戻せません。
                    </MessageBar>

                    <Stack style={{ overflow: 'auto', flex: 1 }} tokens={{ childrenGap: 16 }}>
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Label>削除するファイル（{deletions.length}件）</Label>
                            {deletions.length === 0 ? (
                                <Text variant="small">なし</Text>
                            ) : (
                                deletions.map(file => (
                                    <Text key={file.seq} variant="small">・{displayFileName(file)}</Text>
                                ))
                            )}
                        </Stack>

                        <Stack tokens={{ childrenGap: 4 }}>
                            <Label>アップロードするファイル（{uploads.length}件）</Label>
                            {uploads.length === 0 ? (
                                <Text variant="small">なし</Text>
                            ) : (
                                uploads.map(file => (
                                    <Text key={file.name} variant="small">・{file.name}</Text>
                                ))
                            )}
                        </Stack>
                    </Stack>

                    <Stack horizontalAlign="end" horizontal tokens={{ childrenGap: 10 }}>
                        <DefaultButton
                            text="キャンセル"
                            iconProps={{ iconName: "Cancel" }}
                            onClick={() => onFinished({
                                executed: false,
                                indexerStarted: false,
                                message: "",
                                failedDeletions: [],
                                failedUploads: []
                            })}
                        />
                        <PrimaryButton text="実行する" onClick={startApply} />
                    </Stack>
                </Stack>
            )}

            {phase === "deleting" && (
                <DeleteFilePane files={deletions} onComplete={handleDeleteComplete} />
            )}

            {phase === "uploading" && (
                <FileInfoUI
                    containerSeq={containerSeq}
                    files={uploads}
                    completeButtonText="検索へ反映する"
                    onComplete={handleUploadComplete}
                    onBusyChange={setUploadBusy}
                />
            )}

            {phase === "indexing" && <IndexingPane />}

            {phase === "done" && (
                <Stack tokens={{ childrenGap: 16 }} styles={{ root: { flex: 1, minHeight: 0 } }}>
                    <ApplyResultMessageBar outcome={indexerOutcome} />

                    <Stack tokens={{ childrenGap: 4 }} styles={{ root: { flex: 1 } }}>
                        {0 < deletions.length && (
                            <Text>
                                削除: {deletedCount}件
                                {0 < deleteFailedCount ? `（${deleteFailedCount}件失敗）` : ""}
                            </Text>
                        )}
                        {0 < uploads.length && (
                            <Text>
                                アップロード: {uploadedCount}件
                                {0 < uploadFailedCount ? `（${uploadFailedCount}件失敗）` : ""}
                            </Text>
                        )}
                    </Stack>

                    <Stack horizontalAlign="end" horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton
                            text="閉じる"
                            onClick={() => onFinished({
                                executed: true,
                                indexerStarted: indexerOutcome?.succeeded ?? false,
                                message: indexerOutcome?.message ?? "",
                                failedDeletions,
                                failedUploads
                            })}
                            disabled={uploadBusy}
                        />
                    </Stack>
                </Stack>
            )}
        </ApplyModalFrame>
    )
}
